/**
 * @fileoverview Negotiated customer pricing — the pure calculation shared by
 * the backend charge path and the frontend estimate.
 *
 * CANONICAL SOURCE: backend/src/types/customer-pricing.ts. The frontend copy is
 * written by `pnpm sync:types`; edit only this file.
 *
 * A customer agreement is one of:
 * - `absolute`: an explicit olive amount for an explicit quantity of an
 *   explicit unit ("23 olives per 15 output seconds"). Independent of provider
 *   routing, provider cost cards and the customer list card.
 * - `percentage`: a percentage off the model's published list card.
 * - `multiplier`: the legacy factor over the routed canonical cost. Kept only
 *   so rules that predate this module keep charging what they charged until
 *   the migration manifest supersedes them.
 * - `standard`: an explicit "no negotiated pricing" tombstone for a scope.
 * - `cost_plus`: what the request costs us on the route it is admitted on, at a
 *   chosen cost layer, plus a markup. Written for model `*` it is the
 *   organization's pricing category ("at cost" = net + 0%). Only the backend can
 *   price it: provider cost and contract terms never ship to the browser.
 *
 * Design: docs/plans/negotiated-customer-pricing.md
 */

// ============================================
// Rules
// ============================================

export type CustomerPricingKind = 'absolute' | 'percentage' | 'multiplier' | 'standard' | 'cost_plus'

/** The `model_id` of a rule that covers every model of the organization. A rule for the model itself wins. */
export const CUSTOMER_PRICING_ALL_MODELS = '*'
/**
 * The `model_id` of the organization's agent-work scope (chat turns, workflow
 * runs, utilities): a `cost_plus` rule there is its agent margin over the
 * provider's published rate, a `standard` rule returns it to the platform
 * default. Independent of the media category; never part of the media catalog.
 */
export const CUSTOMER_PRICING_AGENT_SCOPE = 'agent'
/** The organization-wide scopes: not models, never listed as one. */
export function isCustomerPricingOrganizationScope(modelId: string): boolean {
  return modelId === CUSTOMER_PRICING_ALL_MODELS || modelId === CUSTOMER_PRICING_AGENT_SCOPE
}

/**
 * The provider cost layer a cost-plus price starts from: `list` is the
 * primary provider's public price (the serving provider when no primary is designated),
 * `contract` is the serving provider's list less our endpoint discount (what
 * the provider bills per request), `net` is contract less the platform credit back.
 */
export type CustomerPricingCostBasis = 'list' | 'contract' | 'net'

/** The unit an absolute price is quoted in. */
export type CustomerPricingUnit = 'second' | 'image'

/** The unit a request's list cost is expressed in (`asset` = fixed-price models). */
export type CustomerBillableUnit = CustomerPricingUnit | 'asset'

/**
 * Bump when the published customer list card (`MODEL_OLIVE_COSTS`) changes in a
 * way a percentage agreement could observe. Pinned percentage rules record the
 * version at creation and fail closed when it moves.
 *
 * v3 (2026-09-23): the H3 card charges a reference clip's seconds at the take's rate
 * (`videoInputOlivesPerSecond`), so a reference-to-video request's list basis moved.
 */
export const CUSTOMER_LIST_BASIS_VERSION = '2026-09-pricing-v3'

export type CustomerPricingBasis = 'published_list'
export type CustomerPricingBasisPolicy = 'floating' | 'pinned'

/** An olive amount for a quantity of the rule's unit, e.g. `{ olives: 23, per: 15 }`. */
export interface CustomerPricingRate {
  olives: number
  per: number
}

interface CustomerPricingRuleBase {
  id: string
  version: number
  /** Normalized lowercase resolution scope; null = model-wide. */
  scope: string | null
}

export interface AbsoluteCustomerPricingRule extends CustomerPricingRuleBase {
  kind: 'absolute'
  unit: CustomerPricingUnit
  /** Price of the generated output. */
  output: CustomerPricingRate
  /**
   * Price per combined second (generated output + trimmed reference video)
   * when the request carries video references. Null means the agreement does
   * not price reference-to-video and the rule does not match such requests.
   */
  videoReference: CustomerPricingRate | null
}

export interface PercentageCustomerPricingRule extends CustomerPricingRuleBase {
  kind: 'percentage'
  /** Percent off the basis (20 = 20% off; negative = surcharge). */
  percentOff: number
  basis: CustomerPricingBasis
  basisPolicy: CustomerPricingBasisPolicy
  basisVersion: string
}

export interface MultiplierCustomerPricingRule extends CustomerPricingRuleBase {
  kind: 'multiplier'
  multiplier: number
}

export interface StandardCustomerPricingRule extends CustomerPricingRuleBase {
  kind: 'standard'
}

export interface CostPlusCustomerPricingRule extends CustomerPricingRuleBase {
  kind: 'cost_plus'
  costBasis: CustomerPricingCostBasis
  /** Percent on top of the basis (0 = at cost). Never negative: a discount is a cheaper basis. */
  markupPercent: number
}

export type CustomerPricingRule =
  | AbsoluteCustomerPricingRule
  | PercentageCustomerPricingRule
  | MultiplierCustomerPricingRule
  | StandardCustomerPricingRule
  | CostPlusCustomerPricingRule

/**
 * The UI quote guard `/generate` revalidates: the rule the client priced
 * against (nulls = standard pricing).
 */
export interface ExpectedCustomerPricing {
  /** Per-generation maximum displayed by the client; never authorizes a higher debit. */
  maximumOlives?: number
  ruleId: string | null
  ruleVersion: number | null
}

/** A rule row as the backend and the catalog carry it, before selection. */
export interface CustomerPricingRuleVersion {
  rule: CustomerPricingRule
  /** ISO instants. */
  effectiveFrom: string
  effectiveUntil: string | null
}

// ============================================
// Selection
// ============================================

export function normalizeCustomerPricingScope(resolution: string | null | undefined): string | null {
  const trimmed = resolution?.trim()
  return trimmed ? trimmed.toLowerCase() : null
}

function effectiveRuleForScope(
  versions: readonly CustomerPricingRuleVersion[],
  scope: string | null,
  at: Date,
): CustomerPricingRule | null {
  const t = at.getTime()
  let winner: CustomerPricingRuleVersion | undefined
  for (const candidate of versions) {
    if (candidate.rule.scope !== scope) continue
    if (new Date(candidate.effectiveFrom).getTime() > t) continue
    if (
      !winner ||
      candidate.rule.version > winner.rule.version ||
      (candidate.rule.version === winner.rule.version &&
        new Date(candidate.effectiveFrom).getTime() > new Date(winner.effectiveFrom).getTime())
    ) {
      winner = candidate
    }
  }
  if (!winner) return null
  // An expired highest version empties the scope. Older versions never
  // reactivate; the caller falls through to the next broader scope.
  if (winner.effectiveUntil && new Date(winner.effectiveUntil).getTime() <= t) return null
  return winner.rule
}

/**
 * The effective rules per scope at `at`: the request's resolution scope wins
 * over the model-wide scope, which wins over the organization's category. Returned in precedence order so a caller can skip
 * a rule that cannot price its request (see `applyCustomerPricingRule`).
 */
export function selectCustomerPricingRules(
  versions: readonly CustomerPricingRuleVersion[],
  resolution: string | null | undefined,
  at: Date = new Date(),
  /** Versions of the organization's `*` rules (its pricing category); they rank last. */
  categoryVersions: readonly CustomerPricingRuleVersion[] = [],
): CustomerPricingRule[] {
  const scope = normalizeCustomerPricingScope(resolution)
  const ordered: CustomerPricingRule[] = []
  if (scope) {
    const scoped = effectiveRuleForScope(versions, scope, at)
    if (scoped) ordered.push(scoped)
  }
  const modelWide = effectiveRuleForScope(versions, null, at)
  if (modelWide) ordered.push(modelWide)
  // The category stands in for a missing model-wide rule; it never ranks
  // behind one. That keeps the catalog's flat per-model rule list (see
  // `mergeCategoryIntoModelRules`) selecting exactly what this selects.
  const category = modelWide ? null : effectiveRuleForScope(categoryVersions, null, at)
  if (category) ordered.push(category)
  return ordered
}

/**
 * A model's effective rules with the organization's category filling the
 * model-wide scope when the model has none of its own — the list a catalog
 * hands the browser, which then needs no notion of a category.
 */
export function mergeCategoryIntoModelRules(
  modelRules: readonly CustomerPricingRule[],
  categoryRules: readonly CustomerPricingRule[],
): CustomerPricingRule[] {
  const category = categoryRules.find(rule => rule.scope === null)
  if (!category || modelRules.some(rule => rule.scope === null)) return [...modelRules]
  return [...modelRules, category]
}

/** Every scope's effective rule at `at`, for catalogs and agreement pages. */
export function listEffectiveCustomerPricingRules(
  versions: readonly CustomerPricingRuleVersion[],
  at: Date = new Date(),
): CustomerPricingRule[] {
  const scopes = new Set(versions.map(version => version.rule.scope))
  const rules: CustomerPricingRule[] = []
  for (const scope of scopes) {
    const rule = effectiveRuleForScope(versions, scope, at)
    if (rule) rules.push(rule)
  }
  return rules.sort((a, b) => (a.scope ?? '').localeCompare(b.scope ?? ''))
}

/**
 * Precedence order among already-effective rules (one per scope, as the
 * catalog carries them): the request's resolution scope, then model-wide.
 */
export function pickCustomerPricingRules(
  rules: readonly CustomerPricingRule[],
  resolution: string | null | undefined,
): CustomerPricingRule[] {
  const scope = normalizeCustomerPricingScope(resolution)
  const ordered: CustomerPricingRule[] = []
  const scoped = scope ? rules.find(rule => rule.scope === scope) : undefined
  if (scoped) ordered.push(scoped)
  const modelWide = rules.find(rule => rule.scope === null)
  if (modelWide) ordered.push(modelWide)
  return ordered
}

/**
 * The factor a rule applies to a list price, for readers that still think in
 * multipliers (legacy API disclosure). Absolute rules have none.
 */
export function customerPricingRuleFactor(rule: CustomerPricingRule): number | null {
  switch (rule.kind) {
    case 'multiplier':
      return rule.multiplier
    case 'percentage':
      return roundOlives4((100 - rule.percentOff) / 100)
    case 'standard':
      return 1
    case 'absolute':
    case 'cost_plus':
      return null
  }
}

// ============================================
// Exact arithmetic
// ============================================

// No BigInt literals: the frontend copy compiles for an ES2017 target.
function pow10(digits: number): bigint {
  let value = BigInt(1)
  for (let i = 0; i < digits; i += 1) value *= BigInt(10)
  return value
}
const SCALE_DIGITS = 9
const SCALE = pow10(SCALE_DIGITS)
const CHARGE_DIGITS = 4
const CHARGE_SCALE = pow10(CHARGE_DIGITS)

/** Parses a finite non-negative number into a scaled integer without float drift. */
export function toScaledOlives(value: number): bigint {
  if (!Number.isFinite(value) || value < 0) throw new TypeError(`Expected a finite non-negative number, got ${value}`)
  const [whole, fraction = ''] = value.toFixed(SCALE_DIGITS).split('.')
  return BigInt(whole) * SCALE + BigInt(fraction.padEnd(SCALE_DIGITS, '0'))
}

/** Half-up to 0.0001 olive — the single rounding boundary of a charge. */
function roundScaledToCharge(scaled: bigint): number {
  const unit = SCALE / CHARGE_SCALE
  const rounded = (scaled + unit / BigInt(2)) / unit
  return Number(rounded) / Number(CHARGE_SCALE)
}

/** `olives × quantity / per`, exact, at SCALE. */
function rateTimesQuantity(rate: CustomerPricingRate, quantity: number): bigint {
  if (!(rate.per > 0) || !(rate.olives >= 0)) throw new TypeError('Invalid customer pricing rate')
  return (toScaledOlives(rate.olives) * toScaledOlives(quantity)) / toScaledOlives(rate.per)
}

export function roundOlives4(value: number): number {
  return Math.round(value * 10_000) / 10_000
}

// ============================================
// Application
// ============================================

/**
 * What the customer list card would charge for one request, decomposed into
 * the parts an agreement can price separately. `total` is the list charge a
 * customer without an agreement pays (audio, voice and reference-image
 * surcharges included).
 */
export interface CustomerListCostBreakdown {
  /** Portion of list surcharges already covered by a provider reference-token component. */
  referenceTokenSurchargeOlives?: number
  total: number
  unit: CustomerBillableUnit
  /** Output seconds (per-second models), or 1 for per-image and fixed models. */
  outputQuantity: number
  /** Trimmed reference-video seconds the list card bills in addition to output. */
  videoReferenceQuantity: number
  hasVideoReferences: boolean
  /** Audio, voice and reference-image surcharges at list, in olives. */
  surcharges: number
}

export type CustomerPricingTerms =
  | { kind: 'standard' }
  | { kind: 'multiplier'; multiplier: number }
  | {
      kind: 'percentage'
      percentOff: number
      basis: CustomerPricingBasis
      basisPolicy: CustomerPricingBasisPolicy
      basisVersion: string
      /** List cost on the basis card the percentage was applied to. */
      basisCost: number
    }
  | {
      kind: 'absolute'
      unit: CustomerPricingUnit
      output: CustomerPricingRate
      videoReference: CustomerPricingRate | null
      quantities: { output: number; videoReference: number }
      /** List surcharges added on top of the absolute output price. */
      surcharges: number
    }
  | {
      kind: 'cost_plus'
      costBasis: CustomerPricingCostBasis
      markupPercent: number
      /** Provider cost of the HELD quantity at each layer, as estimated at admission. */
      providerCostUsd: CustomerProviderCost
      /**
       * Remaining list surcharges, excluding inputs already in provider cost.
       * Historic snapshots without referenceTokens retain their list surcharge.
       */
      surcharges: number
    }

/** The provider cost of one request at the three layers, on the route it is admitted on. */
export interface CustomerProviderCost {
  /** Fixed input cost already included in the totals. Absent on historic quotes. */
  referenceTokens?: { list: number; contract: number; net: number; listSurchargeOlives: number }
  /** Provider whose public rate defines list; absent on historical quotes (same as provider). */
  listProvider?: string
  list: number
  contract: number
  net: number
  provider: string
  endpointId: string
  /** The provider contract the discounts come from; null when the request is at list. */
  contractReference: string | null
  /**
   * How the provider meters this request's seconds, snapshotted so a measured-duration
   * settlement (an Auto hold) scales the cost by the seconds the provider bills rather than
   * the seconds the customer sees — fal Seedance 2.5 invoices a frame past the take and each
   * reference clip at its whole seconds, never below two-thirds of the take. Absent on quotes
   * admitted before the snapshot existed: those settle on customer seconds, as quoted.
   */
  billedSeconds?: ProviderBilledSecondsRule
}

/** A provider's per-second metering rule; `billedVideoReferenceSeconds` applies the clip half. */
export interface ProviderBilledSecondsRule {
  /** Whether reference clips are billed on top of the generated seconds at all. */
  billsVideoReferences: boolean
  /** Seconds invoiced once per request beyond the take (rendered frames past the duration). */
  extraSeconds?: number
  /** Each reference clip bills its whole seconds (a 9.08 s clip is 9). */
  videoReferenceWholeSeconds?: boolean
  /** The reference clips bill no less than this fraction of the generated seconds, rounded up. */
  videoReferenceMinimumOutputFraction?: number
}

// ============================================
// Cost-based prices in the browser
// ============================================

/** A resolution tier of a customer rate card; the first tier whose `maxHeight` covers the request wins. */
export interface CustomerCostPlusRateTier {
  maxHeight: number
  olivesPerUnit: number
  byQuality?: Record<string, number>
  /** Aspect factors for this tier alone; override the rates' factors. */
  aspectFactors?: Record<string, number>
}

/**
 * What a cost-based rule charges for one route of a model, as finished customer
 * prices: olives per unit with the provider cost, our contract terms and the
 * rule's markup already applied. It is the customer's own price list and carries
 * nothing of how it was derived — no provider cost, discount or markup.
 */
export interface CustomerCostPlusRates {
  /** What one unit is: a generated second, a request, or 1,000 prompt characters. */
  unit: 'second' | 'request' | 'thousand_chars'
  /** Trimmed video-reference seconds bill on top of the generated seconds. */
  billsVideoReferenceSeconds: boolean
  olivesPerUnit: number | null
  byQuality?: Record<string, number>
  tiers?: CustomerCostPlusRateTier[]
  defaultResolution?: string
  /** Rates of a request that carries video references, when they differ. */
  videoInputOlivesPerUnit?: number
  videoInputTiers?: CustomerCostPlusRateTier[]
  /** Seconds billed once per request beyond the generated seconds (rendered frames past the duration). */
  billedExtraSeconds?: number
  /** Factors on the tier or base rate by render quality, when the quality has no rate of its own. */
  qualityFactors?: Record<string, number>
  /** Factors on the rate by aspect ratio; the tier rates are the 16:9 price. */
  aspectFactors?: Record<string, number>
  /** Each reference clip bills its whole seconds (a 9.08 s clip is 9). */
  videoReferenceWholeSeconds?: boolean
  /** The reference clips bill no less than this fraction of the generated seconds, rounded up. */
  videoReferenceMinimumOutputFraction?: number
  /** What each reference image sent with the request adds, once per image. */
  olivesPerReferenceImage?: number
  includedReferenceImages?: number
  olivesPer1kReferenceTokens?: number
}

/** What a browser needs to show a model's cost-based price without asking the server. */
export interface CustomerCostPlusPricing {
  /** Contract-cost image quotes are ceilings, with automatic downward settlement. */
  isMaximum?: boolean
  rates: CustomerCostPlusRates
  /** The rates of a request that carries media inputs, when the model serves it on another route. */
  mediaInputRates?: CustomerCostPlusRates
}

/** The parts of a request that decide its cost-based price. */
export interface CostPlusPriceRequest {
  /** Render quality (`flux3RenderQuality ?? quality`). */
  quality?: string | null
  resolution?: string | null
  hasVideoReferences: boolean
  /** Generated seconds (the sum of the shots of a multi-prompt request); null when unknown. */
  durationSeconds: number | null
  /** Combined trimmed seconds of the video references. */
  videoReferenceSeconds: number
  /** The trimmed seconds of each video reference, when the rates bill them one clip at a time. */
  videoReferenceClipSeconds?: readonly number[]
  promptChars: number
  /** Output aspect ratio, as the generation request sends it (`1:1`). */
  aspectRatio?: string | null
  /** Reference images sent with the request. */
  imageReferenceCount?: number
  billedReferenceTokens?: number
  referenceTokenSurchargeOlives?: number
}

function costPlusResolutionHeight(resolution: string | null | undefined): number | null {
  if (typeof resolution !== 'string') return null
  // Letter-suffixed labels carry no pixel height in their digits ('4k' would parse as 4).
  const normalized = resolution.trim().toLowerCase()
  if (normalized === '4k') return 2160
  if (normalized === '2k') return 1440
  const match = resolution.match(/(\d+)/)
  if (!match) return null
  const height = Number(match[1])
  return Number.isFinite(height) && height > 0 ? height : null
}

/**
 * A cost-based rule's charge for one request, from the customer rates the
 * catalog carries. The server charges from the provider cost of the exact
 * request (`quoteCustomerPrice`), which it rounds in USD first, so this can sit
 * a rounding step (under 0.002 olives) off the charge; `customerPricing.costPlusParity.test.ts`
 * holds the two together. `null` when the rates cannot price the request.
 */
/**
 * The reference-clip seconds a provider bills: the trimmed seconds, each clip floored to whole
 * seconds when the provider meters that way, and no less than the provider's minimum share of
 * the generated seconds when it has one. Mirrors `deriveUnitQuantity` in billing/providerCost.ts.
 */
export function billedVideoReferenceSeconds(
  request: Pick<CostPlusPriceRequest, 'durationSeconds' | 'videoReferenceSeconds' | 'videoReferenceClipSeconds'>,
  rates: Pick<CustomerCostPlusRates, 'videoReferenceWholeSeconds' | 'videoReferenceMinimumOutputFraction'>,
): number {
  const clips = request.videoReferenceClipSeconds ?? [request.videoReferenceSeconds]
  const measured = rates.videoReferenceWholeSeconds
    ? clips.reduce((sum, seconds) => sum + Math.floor(Math.max(0, seconds)), 0)
    : clips.reduce((sum, seconds) => sum + Math.max(0, seconds), 0)
  const minimum =
    rates.videoReferenceMinimumOutputFraction != null && request.durationSeconds != null
      ? Math.ceil(rates.videoReferenceMinimumOutputFraction * request.durationSeconds - 1e-9)
      : 0
  return Math.max(measured, minimum)
}

export function estimateCostPlusCharge(
  rates: CustomerCostPlusRates,
  request: CostPlusPriceRequest,
  surchargeOlives: number,
): number | null {
  let unitOlives: number | null
  const qualityOlives = request.quality ? rates.byQuality?.[request.quality] : undefined
  if (qualityOlives != null) {
    unitOlives = qualityOlives
  } else {
    const tiers = request.hasVideoReferences && rates.videoInputTiers ? rates.videoInputTiers : rates.tiers
    const height = tiers?.length ? costPlusResolutionHeight(request.resolution ?? rates.defaultResolution) : null
    let tierAspectFactors: Record<string, number> | undefined
    if (tiers?.length && height != null) {
      const tier = tiers.find(tier => height <= tier.maxHeight) ?? tiers[tiers.length - 1]
      unitOlives = (request.quality ? tier.byQuality?.[request.quality] : undefined) ?? tier.olivesPerUnit
      tierAspectFactors = tier.aspectFactors
    } else {
      unitOlives = request.hasVideoReferences
        ? (rates.videoInputOlivesPerUnit ?? rates.olivesPerUnit)
        : rates.olivesPerUnit
    }
    // The factors scale whichever rate the resolution selected; a quality with its own rate above skips them.
    if (unitOlives != null) {
      unitOlives *=
        (request.quality ? (rates.qualityFactors?.[request.quality] ?? 1) : 1) *
        (request.aspectRatio ? ((tierAspectFactors ?? rates.aspectFactors)?.[request.aspectRatio] ?? 1) : 1)
    }
  }
  if (unitOlives == null) return null

  let quantity: number | null
  if (rates.unit === 'thousand_chars') {
    quantity = request.promptChars / 1000
  } else if (rates.unit === 'second') {
    const referenceSeconds =
      rates.billsVideoReferenceSeconds && request.hasVideoReferences ? billedVideoReferenceSeconds(request, rates) : 0
    quantity =
      request.durationSeconds == null
        ? null
        : request.durationSeconds + referenceSeconds + (rates.billedExtraSeconds ?? 0)
  } else {
    quantity = 1
  }
  if (quantity == null) return null

  // Reference images are priced once per image, on top of the output.
  const referenceImageOlives =
    (rates.olivesPerReferenceImage ?? 0) *
    Math.max(0, (request.imageReferenceCount ?? 0) - (rates.includedReferenceImages ?? 0))
  // Up to 0.0001 olive, like the charge; the epsilon keeps float noise from adding a step.
  const referenceTokenOlives =
    rates.olivesPer1kReferenceTokens != null
      ? (rates.olivesPer1kReferenceTokens * (request.billedReferenceTokens ?? 0)) / 1000
      : 0
  const remainingSurcharge =
    surchargeOlives - (rates.olivesPer1kReferenceTokens != null ? (request.referenceTokenSurchargeOlives ?? 0) : 0)
  return (
    Math.ceil(
      (unitOlives * quantity + referenceImageOlives + referenceTokenOlives + remainingSurcharge) * 10_000 - 1e-6,
    ) / 10_000
  )
}

/** USD value of one olive; mirrors `OLIVE_VALUE_USD` in shared.ts, which this module must not import. */
const OLIVE_USD = 0.1

export const CUSTOMER_PRICE_ROUNDING = 'half-up-0.0001-final' as const

export interface ImageBillingReceipt {
  status: 'pending' | 'final'
  maximumOlives: number
  chargedOlives: number
  returnedOlives: number
}

/**
 * The committed price of one request. Persisted verbatim on the request,
 * ledger row and generation; settlement reprices from it, never from the
 * rules table.
 */
export interface CustomerPriceQuote {
  /** Only newly admitted eligible images participate; historical quotes stay fixed. */
  imageSettlement?: 'image_cost_cap_v1'
  schemaVersion: 2
  /** Customer list cost (what no agreement charges). Legacy name kept for readers. */
  canonicalCost: number
  finalCharge: number
  /**
   * finalCharge / canonicalCost — informational and for readers that predate
   * `terms`. Never an input to a new charge.
   */
  multiplier: number
  pricingRuleId: string | null
  pricingRuleVersion: number | null
  scope: string | null
  terms: CustomerPricingTerms
  billableQuantity: number
  billableUnit: CustomerBillableUnit
  rounding: typeof CUSTOMER_PRICE_ROUNDING
}

/**
 * True when `rule` can price `breakdown`. An absolute rule must price every
 * component the request needs; the other kinds always match.
 */
export function customerPricingRuleMatches(
  rule: CustomerPricingRule,
  breakdown: CustomerListCostBreakdown,
  options: Pick<ApplyCustomerPricingOptions, 'providerCostKnown'> = {},
): boolean {
  // A model without a provider cost card cannot be priced from cost, so the
  // rule steps aside and the next one (or the standard card) prices it.
  if (rule.kind === 'cost_plus') return options.providerCostKnown !== false
  if (rule.kind !== 'absolute') return true
  if (rule.unit !== breakdown.unit) return false
  if (breakdown.hasVideoReferences && !rule.videoReference) return false
  return true
}

export interface ApplyCustomerPricingOptions {
  /**
   * List cost on the published (provider-independent) card, for percentage
   * rules. Defaults to `breakdown.total` when the caller has no separate basis.
   */
  publishedListCost?: number
  /** The live published-list version; pinned percentage rules fail closed when it moved. */
  listBasisVersion?: string
  /** Cost-plus rules: the request's provider cost. Backend only. */
  providerCost?: CustomerProviderCost
  /**
   * Cost-plus rules: whether the model has a provider cost card at all. `false`
   * makes a cost-plus rule step aside. The browser knows this (it is on the
   * model card) without knowing the cost, so both sides select the same rule.
   */
  providerCostKnown?: boolean
}

/**
 * A cost-plus rule was selected where the provider cost is not available — the
 * browser. The caller shows that the price is set by agreement and still sends
 * the rule as its expectation; the backend quotes the charge.
 */
export class CustomerPricingNeedsServerQuoteError extends Error {
  constructor(readonly rule: CostPlusCustomerPricingRule) {
    super('This price is quoted by the server')
    this.name = 'CustomerPricingNeedsServerQuoteError'
  }
}

export class CustomerPricingBasisMovedError extends Error {
  constructor(
    readonly ruleId: string,
    readonly pinnedVersion: string,
    readonly liveVersion: string,
  ) {
    super('Pinned customer pricing basis has moved; supersede the rule before quoting')
    this.name = 'CustomerPricingBasisMovedError'
  }
}

/**
 * Applies one matching rule to a list-cost breakdown. Throws when the rule
 * cannot price the request — select with `customerPricingRuleMatches` first.
 */
export function applyCustomerPricingRule(
  breakdown: CustomerListCostBreakdown,
  rule: CustomerPricingRule | null,
  options: ApplyCustomerPricingOptions = {},
): CustomerPriceQuote {
  if (!Number.isFinite(breakdown.total) || breakdown.total < 0) {
    throw new TypeError('list cost must be a non-negative finite number')
  }
  const canonicalCost = roundOlives4(breakdown.total)
  const billableQuantity =
    breakdown.unit === 'second' ? breakdown.outputQuantity + breakdown.videoReferenceQuantity : breakdown.outputQuantity
  const base = {
    schemaVersion: 2 as const,
    canonicalCost,
    pricingRuleId: rule?.id ?? null,
    pricingRuleVersion: rule?.version ?? null,
    scope: rule?.scope ?? null,
    billableQuantity,
    billableUnit: breakdown.unit,
    rounding: CUSTOMER_PRICE_ROUNDING,
  }
  const withCharge = (finalCharge: number, terms: CustomerPricingTerms): CustomerPriceQuote => ({
    ...base,
    finalCharge,
    // The legacy rule's own factor, or the effective ratio for the other kinds.
    multiplier:
      terms.kind === 'multiplier'
        ? terms.multiplier
        : canonicalCost > 0
          ? roundOlives4(finalCharge / canonicalCost)
          : 1,
    terms,
  })

  if (!rule || rule.kind === 'standard') return withCharge(canonicalCost, { kind: 'standard' })

  if (rule.kind === 'multiplier') {
    // Byte-identical to the historic formula so a legacy rule keeps charging
    // what it charged until it is superseded.
    return withCharge(roundOlives4(canonicalCost * rule.multiplier), {
      kind: 'multiplier',
      multiplier: rule.multiplier,
    })
  }

  if (rule.kind === 'percentage') {
    if (rule.basisPolicy === 'pinned' && options.listBasisVersion && options.listBasisVersion !== rule.basisVersion) {
      throw new CustomerPricingBasisMovedError(rule.id, rule.basisVersion, options.listBasisVersion)
    }
    const basisCost = roundOlives4(options.publishedListCost ?? breakdown.total)
    const factor = toScaledOlives(100 - rule.percentOff)
    const scaled = (toScaledOlives(basisCost) * factor) / toScaledOlives(100)
    return withCharge(roundScaledToCharge(scaled), {
      kind: 'percentage',
      percentOff: rule.percentOff,
      basis: rule.basis,
      basisPolicy: rule.basisPolicy,
      basisVersion: rule.basisVersion,
      basisCost,
    })
  }

  if (rule.kind === 'cost_plus') {
    const providerCost = options.providerCost
    if (!providerCost) throw new CustomerPricingNeedsServerQuoteError(rule)
    const surcharges = roundOlives4(breakdown.surcharges - (providerCost.referenceTokens?.listSurchargeOlives ?? 0))
    return withCharge(costPlusCharge(providerCost[rule.costBasis], rule.markupPercent, surcharges), {
      kind: 'cost_plus',
      costBasis: rule.costBasis,
      markupPercent: rule.markupPercent,
      providerCostUsd: providerCost,
      surcharges,
    })
  }

  if (!customerPricingRuleMatches(rule, breakdown)) {
    throw new Error(`Absolute customer pricing rule ${rule.id} cannot price this request`)
  }
  const quantities = { output: breakdown.outputQuantity, videoReference: breakdown.videoReferenceQuantity }
  const scaledOutput =
    breakdown.hasVideoReferences && rule.videoReference
      ? rateTimesQuantity(rule.videoReference, quantities.output + quantities.videoReference)
      : rateTimesQuantity(rule.output, quantities.output)
  const scaled = scaledOutput + toScaledOlives(breakdown.surcharges)
  return withCharge(roundScaledToCharge(scaled), {
    kind: 'absolute',
    unit: rule.unit,
    output: rule.output,
    videoReference: rule.videoReference,
    quantities,
    surcharges: roundOlives4(breakdown.surcharges),
  })
}

/**
 * `costUsd × (1 + markup%)` in olives plus the list surcharges, exact. Rounded
 * UP to 0.0001 olive — the one charge that must never land below the cost it covers.
 */
function costPlusCharge(costUsd: number, markupPercent: number, surchargeOlives: number): number {
  if (!(markupPercent >= 0)) throw new TypeError('Cost-plus markup must be zero or more')
  const costWithMarkup = (toScaledOlives(costUsd) * toScaledOlives(100 + markupPercent)) / toScaledOlives(100)
  const scaled = (costWithMarkup * SCALE) / toScaledOlives(OLIVE_USD) + toScaledOlives(surchargeOlives)
  const unit = SCALE / CHARGE_SCALE
  return Number((scaled + unit - BigInt(1)) / unit) / Number(CHARGE_SCALE)
}

/**
 * Selects the first rule in precedence order that can price the request and
 * applies it. `null` rule → standard pricing.
 */
export function quoteWithCustomerPricingRules(
  breakdown: CustomerListCostBreakdown,
  rules: readonly CustomerPricingRule[],
  options: ApplyCustomerPricingOptions = {},
): CustomerPriceQuote {
  const rule = rules.find(candidate => customerPricingRuleMatches(candidate, breakdown, options)) ?? null
  return applyCustomerPricingRule(breakdown, rule, options)
}

// ============================================
// Settlement
// ============================================

/**
 * Reprices an admitted job from its committed quote for a measured quantity
 * (auto-duration refunds). Reads only the snapshot: a rule created after
 * admission can never change what a running job settles at.
 */
export function settleCustomerPrice(
  quote: CustomerPriceQuote,
  actual: CustomerListCostBreakdown,
  options: {
    /** Actual quantity priced on the published list card (percentage terms). */
    publishedListCost?: number
    /** The HELD quantity priced on the same card today; with it the committed basis is scaled, not re-derived. */
    publishedHeldListCost?: number
    /**
     * Cost-plus terms: held and delivered quantity in the provider's billing
     * unit, when it is not the customer's. MiniMax Music 3 is sold per track
     * but billed upstream per second, so its provider cost scales by seconds.
     */
    providerQuantity?: { held: number; actual: number }
  } = {},
): number {
  const terms = quote.terms
  switch (terms.kind) {
    case 'standard':
      return roundOlives4(actual.total)
    case 'multiplier':
      return roundOlives4(roundOlives4(actual.total) * terms.multiplier)
    case 'percentage': {
      // The same basis the hold was quoted on: the published card, never the
      // routed one, or a Fal-routed completion settles above its own quote.
      const published = roundOlives4(options.publishedListCost ?? actual.total)
      const heldToday = options.publishedHeldListCost
      // Scale the COMMITTED basis by actual/held as priced today. A rate-card
      // deployment between admission and completion moves both by the same
      // factor, so it cancels out and the admitted job is not repriced.
      const basis =
        heldToday !== undefined && heldToday > 0
          ? (toScaledOlives(terms.basisCost) * toScaledOlives(published)) / toScaledOlives(roundOlives4(heldToday))
          : toScaledOlives(published)
      const scaled = (basis * toScaledOlives(100 - terms.percentOff)) / toScaledOlives(100)
      return roundScaledToCharge(scaled)
    }
    case 'cost_plus': {
      // The committed cost, scaled to the measured quantity. The contract is
      // never re-resolved: a term that ended mid-job cannot reprice it.
      const held = options.providerQuantity?.held ?? quote.billableQuantity
      const actualQuantity =
        options.providerQuantity?.actual ??
        (actual.unit === 'second' ? actual.outputQuantity + actual.videoReferenceQuantity : actual.outputQuantity)
      const heldCost = terms.providerCostUsd[terms.costBasis]
      const references = terms.providerCostUsd.referenceTokens
      const fixedCost = references?.[terms.costBasis] ?? 0
      const cost = held > 0 ? ((heldCost - fixedCost) * actualQuantity) / held + fixedCost : heldCost
      // Input tokens were sent once; shorter output does not refund those inputs.
      // Old snapshots lack this component and keep their original formula.
      const surcharges =
        actual.surcharges - (references ? (actual.referenceTokenSurchargeOlives ?? references.listSurchargeOlives) : 0)
      return costPlusCharge(cost, terms.markupPercent, roundOlives4(surcharges))
    }
    case 'absolute': {
      const rule: AbsoluteCustomerPricingRule = {
        id: quote.pricingRuleId ?? 'snapshot',
        version: quote.pricingRuleVersion ?? 0,
        scope: quote.scope,
        kind: 'absolute',
        unit: terms.unit,
        output: terms.output,
        videoReference: terms.videoReference,
      }
      return applyCustomerPricingRule(actual, rule).finalCharge
    }
  }
}

/**
 * Reads the legacy per-generation snapshot (`pricingMultiplier` only) as terms
 * so settlement has one code path. Removal condition: no running generation
 * admitted before the customer-pricing deploy (30 days after it ships).
 */
export function legacyMultiplierQuote(input: {
  chargedAmount: number
  canonicalHeldCost: number
  pricingMultiplier?: number
  pricingRuleId?: string | null
  pricingRuleVersion?: number | null
  billableUnit?: CustomerBillableUnit
}): CustomerPriceQuote {
  const explicit =
    typeof input.pricingMultiplier === 'number' &&
    Number.isFinite(input.pricingMultiplier) &&
    input.pricingMultiplier > 0
      ? input.pricingMultiplier
      : undefined
  // A generation from before the multiplier was snapshotted derives its
  // effective factor from the durable hold instead of reverting to list.
  const multiplier = explicit ?? (input.canonicalHeldCost > 0 ? input.chargedAmount / input.canonicalHeldCost : 1)
  return {
    schemaVersion: 2,
    canonicalCost: roundOlives4(input.canonicalHeldCost),
    finalCharge: roundOlives4(input.chargedAmount),
    multiplier,
    pricingRuleId: input.pricingRuleId ?? null,
    pricingRuleVersion: input.pricingRuleVersion ?? null,
    scope: null,
    terms: multiplier === 1 && !input.pricingRuleId ? { kind: 'standard' } : { kind: 'multiplier', multiplier },
    billableQuantity: 1,
    billableUnit: input.billableUnit ?? 'second',
    rounding: CUSTOMER_PRICE_ROUNDING,
  }
}

/** Accepts a persisted quote of either generation and returns the v2 shape. */
export function readCustomerPriceQuote(value: unknown): CustomerPriceQuote | null {
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  if (record.schemaVersion === 2 && record.terms && typeof record.terms === 'object') {
    return record as unknown as CustomerPriceQuote
  }
  const canonicalCost = Number(record.canonicalCost)
  const finalCharge = Number(record.finalCharge)
  if (!Number.isFinite(canonicalCost) || !Number.isFinite(finalCharge)) return null
  const multiplier = Number(record.multiplier)
  return legacyMultiplierQuote({
    chargedAmount: finalCharge,
    canonicalHeldCost: canonicalCost,
    pricingMultiplier: Number.isFinite(multiplier) ? multiplier : undefined,
    pricingRuleId: typeof record.pricingRuleId === 'string' ? record.pricingRuleId : null,
    pricingRuleVersion: typeof record.pricingRuleVersion === 'number' ? record.pricingRuleVersion : null,
    billableUnit:
      record.billableUnit === 'image' || record.billableUnit === 'asset' || record.billableUnit === 'second'
        ? record.billableUnit
        : undefined,
  })
}

// ============================================
// Display helpers
// ============================================

/** Olives per one unit for an absolute rate (23 per 15 → 1.5333…). Display only. */
export function customerPricingRatePerUnit(rate: CustomerPricingRate): number {
  return rate.olives / rate.per
}

/**
 * Describes a rule in operator/customer copy: "23 olives per 15 seconds",
 * "20% off list", "× 0.5679 (legacy)", "standard pricing".
 */
export function describeCustomerPricingRule(rule: CustomerPricingRule): string {
  switch (rule.kind) {
    case 'absolute': {
      const unit = rule.unit === 'second' ? 'seconds' : 'images'
      const output = `${rule.output.olives} olives per ${rule.output.per} ${unit}`
      return rule.videoReference
        ? `${output}; ${rule.videoReference.olives} olives per ${rule.videoReference.per} combined seconds with video references`
        : output
    }
    case 'percentage':
      return `${rule.percentOff}% off published list (${rule.basisPolicy}, ${rule.basisVersion})`
    case 'multiplier':
      return `× ${rule.multiplier} of routed canonical cost (legacy)`
    case 'standard':
      return 'standard pricing'
    case 'cost_plus':
      // Customer-facing surfaces read this: name the arrangement, never our provider terms.
      return rule.markupPercent === 0 ? 'provider cost' : `provider cost + ${rule.markupPercent}%`
  }
}
